import { Alert, Button, Form, Steps, Tooltip } from 'antd';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { FormInstance } from 'antd';
import { HelpCircle } from 'lucide-react';
import { useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { TwoColumnLayout } from '@/components/TwoColumnLayout';
import {
  isSectionHidden,
  isSectionVisible,
  stepHasScrollingBody,
  stepIndexOf,
  type SectionDescriptor,
  type StepDescriptor,
} from '@/lib/sectionRegistry';
import { SECTION_SCROLL_BODY } from '@/lib/twoColumn';
import { cn } from '@/lib/utils';

/**
 * ─── LAYER: component (shared) ───────────────────────────────────────────────────
 * THE INTAKE SHELL. A stepped, two-column form rendered FROM a section registry
 * (`lib/sectionRegistry.ts`) rather than from JSX position — the stepper, the section
 * cards, the class-hide, the Back/Next pair and the slots for a header, a right rail and a
 * footer.
 *
 * Two consumers: `pages/ArrangementBuilder` (the PA intake) and
 * `pages/StandingOrderBuilder` (round the clock). Everything that differs between them is
 * a descriptor; everything that is the same is here.
 *
 * ⚠️ IT MAPS OVER EVERY DESCRIPTOR AND HIDES WITH A CLASS. It must never `.filter()` first
 * — see `lib/sectionRegistry.ts`'s header for the whole of why, and note that the failure
 * is silent: six derivations quietly read zero and nothing mentions visibility.
 *
 * ⚠️ IT OWNS NO STATE AND NO DATA. The step is the page's `useState`, the form instance is
 * the page's, the queries are the page's. This component exists to be the same shape twice,
 * not to be clever about either.
 *
 * ⚠️ ONE VALUE EXPORT. `react-refresh/only-export-components` is a warning and
 * `pnpm lint` runs `--max-warnings 0`, so a second exported const here fails the build.
 */
export type IntakeFormProps<TState, TStep extends string, TActions, TValues extends object> = {
  form: FormInstance<TValues>;
  initialValues: TValues;
  disabled: boolean;
  onValuesChange: () => void;
  /**
   * ⚠️ [C1f §6] A FIELD WAS LEFT. Omit it and nothing is attached at all.
   *
   * `focusout` bubbles, so one handler on the `<form>` sees every field being left — which is
   * the frequency an autosave can afford and a keystroke is not. It is a PROP rather than
   * behaviour because the round-the-clock form saves EXPLICITLY (`StandingOrderBuilder`:
   * saving is what starts real orders being generated) and must stay byte-identical.
   */
  onFieldBlur?: () => void;

  steps: readonly StepDescriptor<TStep>[];
  step: number;
  /** ⚠️ A SET-STATE, so Back and Next keep their functional updaters rather than reading a stale step. */
  onStepChange: Dispatch<SetStateAction<number>>;
  labels: { back: string; next: string };

  sections: readonly SectionDescriptor<TState, TStep, TActions>[];
  state: TState;
  /** ⚠️ SEPARATE FROM `state`, so `validate` and `visibleWhen` cannot reach it. */
  actions: TActions;
  /** The page owns the id→node Map; `focusLastIn` and the checklist's jump both read it. */
  registerSection: (id: string, node: HTMLElement) => void;

  /** The `PageHeader`. Outside the scrolling form, so the title never scrolls away. */
  header: ReactNode;
  /** The right column's contents. `TwoColumnLayout` caps its height — see below. */
  right: ReactNode;
  /**
   * What the summary bar calls the right column below the split, where it collapses.
   * A noun phrase naming the SECTION, because `aria-expanded` already carries the state.
   */
  rightLabel?: string;
  /**
   * ⚠️ [amended D4.1] Omit and the right column is always there, exactly as before. Pass a
   * boolean and it becomes a disclosure the PAGE owns — closed at every width, opened from
   * a control in the page header. See `TwoColumnLayout`'s header for why closed is a class
   * and never an unmount.
   */
  rightOpen?: boolean;
  /** The id the page's toggle points `aria-controls` at. */
  rightId?: string;
  /**
   * ⚠️ A DIRECT CHILD OF THE ROOT, never wrapped: the action bar is a footer, not a sticky.
   *
   * It is a RENDER PROP because the Back/Next pair belongs in it and belongs to this
   * component — the page decides where in its bar they sit, and gets the same pair either
   * way rather than building a second one.
   */
  footer: (slots: { stepper: ReactNode }) => ReactNode;
  /**
   * ⚠️ [P4-UX] OPT-IN, AND THE REASON IS A REAL TEST RATHER THAN CAUTION.
   *
   * `Next` used to be a pure index bump, so a required Name with a red asterisk on step 1
   * let you straight past it and the refusal surfaced two steps later at "Save and start".
   * Gating it uses the registry's OWN `validate` at the step boundary — no second
   * validation authority, and `validate` still receives only the pure state.
   *
   * It is a prop rather than the default because ArrangementBuilder's own suite clicks
   * `step-next` on a freshly opened page where step 1 legitimately has blockers
   * (`ArrangementBuilder.test.tsx`, "opens on step 1, and step 2 is where the force grid
   * lives"). Turning this on globally would fail that test — which is the suite correctly
   * describing a deliberate difference between the two forms, not a test to edit.
   */
  gateNextOnStep?: boolean;
  /** Modals, alerts — anything that renders outside the form and outside the bar. */
  children?: ReactNode;
};

export const IntakeForm = <TState, TStep extends string, TActions, TValues extends object>({
  form,
  initialValues,
  disabled,
  onValuesChange,
  onFieldBlur,
  steps,
  step,
  onStepChange,
  labels,
  sections,
  state,
  actions,
  registerSection,
  header,
  right,
  rightLabel,
  rightOpen,
  rightId,
  footer,
  gateNextOnStep = false,
  children,
}: IntakeFormProps<TState, TStep, TActions, TValues>) => {
  const [stepBlockers, setStepBlockers] = useState<readonly string[]>([]);
  /**
   * ⚠️ [C1f §5] WHETHER THE STEP ON SCREEN OWNS ITS OWN OVERFLOW.
   *
   * A STATE branch on the step, which §8 permits and which answers the same under jsdom as in
   * a browser — unlike a width branch, which `vitest.setup.ts`'s permanent
   * `matchMedia: { matches: false }` would silently take the narrow arm of in every test.
   */
  const bodyScrolls = stepHasScrollingBody(sections, steps, step, state);

  /** The blockers the CURRENT step is carrying, from the registry, in registry order. */
  const blockersOnThisStep = (): readonly string[] =>
    sections
      .filter((descriptor) => stepIndexOf(steps, descriptor.step) === step && isSectionVisible(descriptor, state))
      .flatMap((descriptor) => descriptor.validate(state).blockers);

  const clearStepBlockers = (): void => setStepBlockers([]);

  const stepper = (
    <div className="flex items-center gap-1" data-testid="step-nav">
      {/*
        ⚠️ `type="button"`. Inside a `<Form>` a bare button submits it, and AntD's default
        is `button` only because rc-field-form sets it — not something to rely on for a
        control whose whole job is to NOT submit.
      */}
      <Button
        type="default"
        htmlType="button"
        icon={<ChevronLeft className="size-4" />}
        disabled={step === 0}
        data-testid="step-back"
        onClick={() => {
          // Going BACK is never refused: the operator is returning to fix something.
          setStepBlockers([]);
          onStepChange((current) => Math.max(0, current - 1));
        }}
      >
        {labels.back}
      </Button>
      <Button
        type="default"
        htmlType="button"
        disabled={step === steps.length - 1}
        data-testid="step-next"
        onClick={() => {
          const blocking = gateNextOnStep ? blockersOnThisStep() : [];
          if (blocking.length > 0) {
            setStepBlockers(blocking);
            return;
          }
          setStepBlockers([]);
          onStepChange((current) => Math.min(steps.length - 1, current + 1));
        }}
      >
        {labels.next}
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );

  return (
    /* `h-full` + `min-h-0`: the page is exactly as tall as <main>, and scrolls inside. */
    <div className="flex h-full min-h-0 flex-col gap-4">
      {header}

      {/* ⚠️ THE ONE SCROLL CONTAINER ON THIS PAGE. `min-h-0` is what lets a flex child
        shrink below its content and actually scroll. */}
      <Form
        form={form}
        layout="vertical"
        className="min-h-0 flex-1 overflow-y-auto"
        disabled={disabled}
        initialValues={initialValues}
        onBlur={onFieldBlur}
        onValuesChange={() => {
          clearStepBlockers();
          onValuesChange();
        }}
      >
        <TwoColumnLayout
          variant="form"
          /* ⚠️ [C1f §5] Only on a step that HAS a bounded body — see `stepHasScrollingBody`. */
          primaryFills={bodyScrolls}
          left={
            <div className={cn('flex flex-col gap-4 pb-2', bodyScrolls && 'h-full min-h-0')}>
              {/*
              Clickable, always, in both directions — the steps are steps, not a wizard.
              `size="small"` and `type="navigation"` keep it to one line on a laptop, which
              is the width this column has.
            */}
              <Steps
                size="small"
                type="navigation"
                current={step}
                onChange={onStepChange}
                className="shrink-0 rounded-lg border border-card-outline bg-card px-2"
                data-testid="form-steps"
                items={steps.map((formStep) => ({ title: formStep.title }))}
              />

              {sections.map((descriptor) => (
                <section
                  key={descriptor.id}
                  ref={(node) => {
                    // ⚠️ A BLOCK BODY. `Map.set` returns the Map, and React 19 reads a
                    // non-function return from a callback ref as a cleanup function.
                    if (node) registerSection(descriptor.id, node);
                  }}
                  id={`section-${descriptor.id}`}
                  aria-labelledby={`heading-${descriptor.id}`}
                  /*
                   * ⚠️ HIDDEN WITH A CLASS, NEVER UNMOUNTED, AND BOTH HALVES ARE LOAD-BEARING.
                   *
                   * Unmounting is the obvious implementation and it breaks a watched form
                   * specifically: `Form.useWatch`'s selector is called with
                   * `getFieldsValue()`, which answers REGISTERED FIELDS ONLY. A step that is
                   * not rendered has no registered fields, so the grid, the checklist and the
                   * right rail's counts all read zero — the defect CLAUDE.md §8 records once.
                   *
                   * And it is the Tailwind CLASS rather than the `hidden` ATTRIBUTE because
                   * jsdom never loads `styles/index.css` (§8 again). Under test every section
                   * stays in the accessibility tree, so the suite addresses the form as the
                   * single form it still is.
                   */
                  /*
                   * ⚠️ [amended D4.1] `surface: 'bare'` DROPS THE CARD AND NOTHING ELSE.
                   * The id, the ref, `aria-labelledby` and the step's class-hide are the
                   * same on both branches, so a bare section is addressable, registrable
                   * and hideable exactly as a carded one is. See `SectionDescriptor`.
                   */
                  className={cn(
                    descriptor.surface !== 'bare' && 'rounded-lg border border-card-outline bg-card p-4',
                    /* ⚠️ [C1f §5] A bounded body needs its section to be the column that bounds it. */
                    descriptor.body === 'scroll' && 'flex min-h-0 flex-1 flex-col',
                    isSectionHidden(descriptor, steps, step, state) && 'hidden',
                  )}
                >
                  {/*
                  ⚠️ THE ACTION IS A SIBLING OF THE <h2>, NEVER A CHILD. Nesting folds the
                  button's label into the section's accessible name, and every
                  `getByRole('region')` starts reading "Places Add another place".

                  `min-h-6` is `controlHeightSM`, so a section WITH an action and one without
                  keep the same header height and the cards do not jitter as it appears.
                */}
                  {/* ⚠️ [C1f §5] `shrink-0` IS UNCONDITIONAL AND INERT IN THE FLOW CASE.
                      `flex-shrink` only applies inside a bounded flex container, so on an
                      auto-height section nothing shrinks and the class is a no-op — while its
                      ABSENCE is what would silently squash the header the day a second section
                      joined a bounded step. */}
                  <div className="mb-3 flex min-h-6 shrink-0 flex-wrap items-center justify-between gap-2">
                    <h2 id={`heading-${descriptor.id}`} className="text-sm font-semibold tracking-tight text-heading">
                      {descriptor.title}
                      {descriptor.tooltip !== undefined && (
                        <Tooltip title={descriptor.tooltip}>
                          {/*
                          ⚠️ INSIDE the <h2> and `aria-hidden`, which is the opposite of the
                          action's rule and for the same reason: a focusable control here
                          would be a second tab stop per section, and its label WOULD join
                          the section's accessible name. The sentence reaches a reader
                          through the field tooltips and the checklist, never through this.
                        */}
                          <HelpCircle
                            className="ml-1 inline size-3.5 align-[-2px] text-muted-foreground"
                            aria-hidden
                            data-testid={`section-tooltip-${descriptor.id}`}
                          />
                        </Tooltip>
                      )}
                    </h2>
                    {descriptor.action?.(state, actions)}
                  </div>
                  {descriptor.body === 'scroll' ? (
                    <div className={SECTION_SCROLL_BODY} data-testid={`section-body-${descriptor.id}`}>
                      {descriptor.render(state, actions)}
                    </div>
                  ) : (
                    descriptor.render(state, actions)
                  )}
                </section>
              ))}
            </div>
          }
          /*
           * ⚠️ [amended UX-FINAL] THE PRIMITIVE CAPS THE STICKY COLUMN NOW, not the page.
           * This wrapper applied `STICKY_CAP`, a constant pinned at 880 — on a `form` layout
           * that splits at 920, so between the two the column was viewport-capped while still
           * stacked full-width. A page cannot get that right because the threshold belongs to
           * the VARIANT; `lib/twoColumn.ts`'s `TWO_COLUMN_COLLAPSE` has it.
           */
          right={right}
          {...(rightOpen === undefined ? {} : { secondaryOpen: rightOpen })}
          {...(rightId ? { secondaryId: rightId } : {})}
          secondaryLabel={rightLabel}
        />
      </Form>

      {/*
      ⚠️ [P3b.2] BACK AND NEXT LIVE IN THE ACTION BAR, BESIDE THE SAVE STATE.
      They used to sit at the foot of the scrolling column, which meant the operator had to
      scroll to the bottom of a step to move to the next one — and then scroll back up to
      read it. In the bar they are always reachable, and they sit with the other two things
      that are about the FORM rather than about a field. The page supplies the bar; this
      component supplies the pair, so both intake forms get the same one.
    */}
      {/*
        ⚠️ [P4-UX] THE REFUSAL SITS WITH THE CONTROL THAT REFUSED, above the bar carrying
        Next. Cleared on any edit and on any step change, so it never outlives the problem
        it describes.
      */}
      {stepBlockers.length > 0 && (
        <Alert
          type="error"
          showIcon
          className="mb-2 shrink-0"
          data-testid="step-blocked"
          message={stepBlockers.join(' ')}
        />
      )}

      {footer({ stepper: <>{stepper}</> })}
      {children}
    </div>
  );
};
